import {
  Autocomplete,
  Button,
  CheckboxField,
  Divider,
  Flex,
  Grid,
  Text,
} from "@aws-amplify/ui-react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import { API, graphqlOperation } from "aws-amplify";
import toast from "react-hot-toast";
import PermissionIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

import {
  getDashboard,
  listSharedDashboards,
  listUsers,
  sharedDashboardsByDashboardID,
} from "graphql/queries";
import { globalStrings } from "config/translations/es";
import { LayoutContext } from "layout/context/LayoutContext";
import { getUserEmail } from "graphql/custom-queries";
import { CPopover } from "@coreui/react";
import {
  createSharedDashboard,
  deleteSharedDashboard,
  updateSharedDashboard,
} from "graphql/mutations";

const SharedDashboardForm = ({
  dashboardID,
  onSuccess,
  isOwner = false,
  permissions = [],
}) => {
  const { me } = useContext(LayoutContext);
  const [addUserVal, setAddUserVal] = useState("");
  const [owner, setOwner] = useState();
  const [dashboardUsers, setDashboardUsers] = useState([]);
  const [initDashboardUsers, setInitDashboardUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const resetFormValues = () => {
    setDashboardUsers(initDashboardUsers);
  };

  const onSubmitForm = async () => {
    try {
      const sharedToCreate = dashboardUsers?.filter(
        (dash) =>
          !initDashboardUsers?.some((initDash) => initDash.id === dash.id)
      );
      const sharedToDelete = initDashboardUsers?.filter(
        (initDash) => !dashboardUsers?.some((dash) => dash.id === initDash.id)
      );
      const sharedToUpdate = dashboardUsers?.filter(
        (dash) =>
          !sharedToCreate?.some((shared) => shared.id === dash.id) &&
          !sharedToDelete?.some((shared) => shared.id === dash.id)
      );
      const promises = [];

      // Update Shared Dashboards
      sharedToDelete?.forEach(async (shared) => {
        const records = (
          await API.graphql({
            query: listSharedDashboards.replaceAll("__typename", ""),
            variables: {
              filter: {
                and: [
                  { userID: { eq: shared.id } },
                  { dashboardID: { eq: dashboardID } },
                ],
              },
            },
          })
        )?.data?.listSharedDashboards?.items;
        promises.push(
          API.graphql({
            query: deleteSharedDashboard.replaceAll("__typename", ""),
            variables: {
              input: {
                id: records[0].id,
              },
            },
          })
        );
      });

      sharedToCreate?.forEach(async (shared) => {
        promises.push(
          API.graphql({
            query: createSharedDashboard.replaceAll("__typename", ""),
            variables: {
              input: {
                userID: shared.id,
                dashboardID,
                permissions: shared.permissions,
              },
            },
          })
        );
      });

      sharedToUpdate?.forEach(async (shared) => {
        const records = (
          await API.graphql({
            query: listSharedDashboards.replaceAll("__typename", ""),
            variables: {
              filter: {
                and: [
                  { userID: { eq: shared.id } },
                  { dashboardID: { eq: dashboardID } },
                ],
              },
            },
          })
        )?.data?.listSharedDashboards?.items;
        promises.push(
          API.graphql({
            query: updateSharedDashboard.replaceAll("__typename", ""),
            variables: {
              input: {
                id: records[0].id,
                permissions: shared.permissions,
              },
            },
          })
        );
      });

      await Promise.all(promises);
      toast.success(strings.successUpdated);

      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(strings.responses.errorUploading);
    }
  };

  const onDelete = (id) => {
    setDashboardUsers((prev) => prev?.filter((dash) => dash.id !== id));
  };

  const onSelect = (id) => {
    const userSel = users?.find((us) => us.id === id);
    setDashboardUsers((prev) => [
      ...prev,
      { id: userSel.id, email: userSel.email, permissions: ["view"] },
    ]);
    setAddUserVal("");
  };

  const onChangeSelect = (e) => {
    const { value } = e.target;
    if (value) fetchUsers(value);
    setAddUserVal(value);
  };

  const onChangePermissions = (userId, name, value) => {
    setDashboardUsers((prev) => {
      const listUpdated = [...prev];
      const indexOf = listUpdated.findIndex(
        (listItem) => listItem.id === userId
      );

      if (value)
        listUpdated[indexOf] = {
          ...listUpdated[indexOf],
          permissions: [...listUpdated[indexOf].permissions, name],
        };
      else
        listUpdated[indexOf] = {
          ...listUpdated[indexOf],
          permissions: listUpdated[indexOf].permissions?.filter(
            (per) => per !== name
          ),
        };

      return listUpdated;
    });
  };

  async function getOwner(id) {
    try {
      const dashboardData = await API.graphql(
        graphqlOperation(getDashboard, { id })
      );
      const dashboardItem = dashboardData.data.getDashboard;

      const userData = await API.graphql(
        graphqlOperation(getUserEmail, { id: dashboardItem.ownerID })
      );
      const userItem = userData.data.getUser;

      setOwner(userItem);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  async function fetchUsers(email) {
    let filterObj = {};
    try {
      setUsersLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ companyID: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ companyID: { eq: cp } })),
          ],
        };

      const usersData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: {
            and: [{ email: { contains: email } }],
            ...filterObj,
          },
        })
      );
      const usersItems = usersData.data.listUsers.items;
      setUsers(usersItems);
    } catch (err) {
      toast.error(strings.responses.errorFetchData);
    } finally {
      setUsersLoading(false);
    }
  }

  async function fetchSharedDashboard(id) {
    try {
      const dashboardsData = await API.graphql(
        graphqlOperation(sharedDashboardsByDashboardID, {
          dashboardID: id,
        })
      );
      const dashboardsItems =
        dashboardsData.data.sharedDashboardsByDashboardID.items;

      if (dashboardsItems?.length <= 0) return;

      const promises = [];
      promises.push(
        ...dashboardsItems?.map((dashIt) =>
          API.graphql(
            graphqlOperation(getUserEmail, {
              id: dashIt.userID,
            })
          )
        )
      );
      const usersData = await Promise.all(promises);

      const list = dashboardsItems?.map((it) => ({
        id: it.userID,
        email: usersData?.find((userIt) => userIt.data.getUser.id === it.userID)
          ?.data.getUser.email,
        permissions: it.permissions,
      }));

      setInitDashboardUsers(list ?? []);
      setDashboardUsers(list ?? []);
    } catch (err) {
      toast.error(strings.responses.errorFetchData);
    }
  }

  useEffect(() => {
    getOwner(dashboardID);
    fetchSharedDashboard(dashboardID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardID]);

  return (
    <Grid rowGap="15px" columnGap="15px" padding="20px">
      <Autocomplete
        placeholder={strings.fields.addUser}
        options={users
          ?.filter(
            (user) =>
              !dashboardUsers?.some((dash) => dash.id === user.id) &&
              owner.id !== user.id
          )
          ?.map((it) => ({
            id: it.id,
            label: it.email,
          }))}
        value={addUserVal}
        onChange={onChangeSelect}
        onClear={() => setAddUserVal("")}
        isLoading={usersLoading}
        onSelect={({ id }) => onSelect(id)}
        menuSlots={{ Empty: globalStrings.noResults }}
      ></Autocomplete>
      <Grid>
        {owner && (
          <>
            <Flex
              padding="10px"
              alignItems="center"
              justifyContent="space-between"
            >
              <Flex gap="5px">
                <Text variation="primary" as="p" fontWeight={500}>
                  {owner.email}
                </Text>
                {isOwner && (
                  <Text variation="tertiary" as="p" fontWeight={500}>
                    ({strings.you})
                  </Text>
                )}
              </Flex>
              <Text variation="tertiary" as="p" fontWeight={500}>
                {strings.owner}
              </Text>
            </Flex>
            <Divider className="mt-2 mb-2" />
          </>
        )}
        {dashboardUsers?.map((dashUs) => (
          <>
            <Flex
              padding="10px"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text variation="primary" as="p" fontWeight={500}>
                {dashUs.email}
              </Text>
              <Flex gap="10px" justifyContent="flex-end">
                <CPopover
                  trigger="click"
                  content={
                    <Grid>
                      {Object.entries(strings.fields.permissions.options)
                        .filter((perObj) =>
                          isOwner
                            ? true
                            : permissions?.some((per) => per === perObj[0])
                        )
                        .map((perObj) => (
                          <CheckboxField
                            label={perObj[1]}
                            checked={
                              dashUs.permissions?.some(
                                (dashPer) => dashPer === perObj[0]
                              ) || false
                            }
                            disabled={perObj[0] === "view"}
                            onChange={(e) =>
                              onChangePermissions(
                                dashUs.id,
                                perObj[0],
                                e.target.checked
                              )
                            }
                          ></CheckboxField>
                        ))}
                    </Grid>
                  }
                  placement="top"
                >
                  <Button
                    size="small"
                    style={styles.buttonSubmit}
                    children={<PermissionIcon />}
                    onClick={() => {}}
                  ></Button>
                </CPopover>
                {isOwner && (
                  <Button
                    size="small"
                    colorTheme="error"
                    style={styles.buttonSubmit}
                    children={<DeleteIcon />}
                    onClick={() => onDelete(dashUs.id)}
                  ></Button>
                )}
              </Flex>
            </Flex>
            <Divider className="mt-2 mb-2" />
          </>
        ))}
      </Grid>
      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={strings.fields.buttons.save}
          onClick={onSubmitForm}
          variation="primary"
        ></Button>
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      addUser: "Agregar usuario",
      buttons: {
        save: "Guardar",
        reset: "Restablecer",
      },
      permissions: {
        options: {
          view: "Ver",
          edit: "Editar",
          share: "Compartir",
        },
      },
    },
    responses: {
      errorFetchData: "Error al consultar datos",
      errorUploading: "Error al guardar datos",
    },
    permissions: "Permisos",
    owner: "Propietario",
    you: "Tú",
    successUpdated: "Tablero actualizado con éxito",
  },
});

const styles = {
  buttonSubmit: {
    justifySelf: "flex-end",
    width: "fit-content",
    height: "fit-content",
  },
};

export default SharedDashboardForm;
